import React, { useMemo } from 'react';
import isPlainObject from 'lodash/isPlainObject';
import clamp from 'lodash/clamp';
import round from 'lodash/round';
import uniqueId from 'lodash/uniqueId';
import { I18n } from 'common/i18n';

import './index.scss';
import { ForgeSlider } from '@tylertech/forge-react';
import { ISliderChangeEventData, ISliderRangeChangeEventData } from '@tylertech/forge';

interface MinMax {
  min: number;
  max: number;
}

interface StartEnd {
  start: number;
  end: number;
}

/**
 * Type predicate for StartEnd
 * @link https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
 * @todo We don't check 'end' because the original code didn't,
 * and I don't want to change the behavior right now.
 */
function instanceOfStartEnd(object: any): object is StartEnd {
  return isPlainObject(object) && 'start' in object;
}

interface Props {
  disabled?: boolean;
  showLabels?: boolean;
  /** To format the labels */
  formatLabel?: (label: number) => string;
  /** precision for round(#, precision) in formatLabel */
  precision?: number;
  /** The minimum value selectable */
  rangeMin: number;
  /** The maximum value selectable */
  rangeMax: number;
  /** The increment that the user can move the handle(s) */
  step?: number;
  /**
   * The value the slider should show. It comes in two flavors:
   * - The object flavor lets you select two values with the
   *   same slider. Renders two handles. (See the shape.)
   * - The number flavor is a single selection and only renders
   *   one handle.
   */
  value?: number | MinMax | StartEnd;
  /**
   * The change event is fired when the slider is dragged
   * or keyboard-navigated. The callback's value depends
   * on how props.value was set. If it was an object, you'll
   * get an object. If it was a number, you'll get a number.
   */
  onChange: (value: number | MinMax | StartEnd) => void;
}

const transformValue = (
  value: undefined | number | MinMax | StartEnd,
  rangeMin: number,
  rangeMax: number,
): number | MinMax => {
  if (instanceOfStartEnd(value)) {
    return {
      min: value.start,
      max: value.end,
    } as MinMax;
  } else if (typeof value === 'number') {
    return clamp(value, rangeMin, rangeMax);
  } else {
    return rangeMax;
  }
};

export const Slider: React.FC<Props> = ({
  disabled = false,
  formatLabel,
  showLabels = true,
  precision = 2,
  rangeMin,
  rangeMax,
  step = 5,
  value: propValue,
  onChange,
}) => {
  const labelId = useMemo(() => `slider-label-${uniqueId()}`, []);

  const sliderValue = transformValue(propValue, rangeMin, rangeMax);

  const handleChange = (event: CustomEvent<ISliderChangeEventData>) => {
    const newValue = event.detail.value;
    onChange(newValue);
  };

  const handleRangeChange = (event: CustomEvent<ISliderRangeChangeEventData>) => {
    // The range comes in as a start end object so we want to pass the change back in the same structure
    // as the user provided it.
    const newValue = {
      start: event.detail.valueStart,
      end: event.detail.valueEnd,
    };
    onChange(newValue);
  };

  const formatLabelDefault = (label: number) => {
    return round(label, precision);
  };

  const formatSliderLabel = formatLabel || formatLabelDefault;
  const showRangeSlider = sliderValue && typeof sliderValue === 'object';

  const sliderProps = {
    'data-testid': 'common-component-forge-slider',
    'aria-labelledby': labelId,
    className: 'forge-slider',
    min: rangeMin,
    max: rangeMax,
    step: step,
    disabled: disabled,
  };

  if (showRangeSlider) {
    Object.assign(sliderProps, {
      range: true,
      labelStart: sliderValue.min,
      labelEnd: sliderValue.max,
      valueEnd: sliderValue.max,
      valueStart: sliderValue.min,
      'on-forge-slider-range-change': handleRangeChange,
    });
  } else {
    Object.assign(sliderProps, {
      label: sliderValue,
      value: sliderValue,
      'on-forge-slider-change': handleChange,
    });
  }

  return (
    <div className="slider-container">
      <div className="input-range-slider">
        <label id={labelId} className="screenreader-only">
          {I18n.t('shared.components.filter_bar.slider')}
        </label>
        <ForgeSlider {...sliderProps} />
      </div>
      {showLabels && (
        <div className="slider-label-container">
          <span className="slider-label forge-typography--label1">{formatSliderLabel(rangeMin)}</span>
          <span className="slider-label forge-typography--label1">{formatSliderLabel(rangeMax)}</span>
        </div>
      )}
    </div>
  );
};

export default Slider;
